import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { useLiveChatCRUDActions } from '@mm/state/actions/LiveChatCRUDActions';
import { useSessionCRUDActions } from '@mm/state/actions/SessionCRUDActions';
import { defaultPageIdSelector } from '@mm/state/selectors/pageSelectors';
import {
  activeChatbotIdSelector,
  connectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useMachine } from '@xstate/react';
import queryString from 'query-string';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useLiveChatContext } from './LiveChatContext';
import {
  createLivechatMachine,
  initialFetchStatus,
  LcContext,
  LcEventSchema,
} from './liveChatMachine';
import { useLiveChatServiceObserver } from './hooks/use-live-chat-service';

export const LivechatContextAdder = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const botId = useSelector(activeChatbotIdSelector);
  const remoteFbPageId = useSelector(
    (state: StoreState) => state.connectedPage.pageDetails?.remoteId || '',
  );
  const liveChatCRUDActions = useLiveChatCRUDActions();
  const contactsCRUDActions = useContactsCRUDActions();
  const sessionCRUDActions = useSessionCRUDActions();

  const { userId, userAuthToken } = useSelector((state: StoreState) => ({
    userId: state.session.session?.id,
    userAuthToken: state.session.session?.authToken,
  }));

  const connectedPage = useSelector(connectedPageSelector);

  const location = useLocation();
  const history = useHistory();

  const parsed = queryString.parse(location.search.toLowerCase());
  const [, , , path, parsedRemoteUserId] = location.pathname.split('/');

  let newRemoteUserId: string | undefined;

  if (!parsed.remoteuserid && path === 'live-chat' && parsedRemoteUserId) {
    newRemoteUserId = parsedRemoteUserId;
  }
  newRemoteUserId =
    typeof parsed.remoteuserid === 'string'
      ? parsed.remoteuserid.replace(/ /g, '+')
      : undefined;

  const activeBotDefaultPageId = useSelector((state: StoreState) =>
    defaultPageIdSelector(state)?.toString(),
  );

  const botLanguage = useSelector(
    (state: StoreState) =>
      (state.chatbot.activeChatbot && state.chatbot.activeChatbot.language) ||
      'English',
  );

  const lcMachine = useMemo(() => {
    return createLivechatMachine(
      {
        lastSSEUpdate: Date.now(),
        userId,
        userAuthToken,
        sessionSuffix: undefined,
        onlineRemoteUserIds: new Set(),
        remoteUserId: newRemoteUserId,

        conversationsRefs: {},
        contactsRefs: {},
        activeContact: undefined,
        remoteFbPageId,
        searchString: '',
        foundConversations: undefined,
        foundContacts: [],
        mostRecentConversationsRemoteUserIds: new Set<string>(),
        filteredConversationRemoteUserIds: new Set<string>(),
        batchConversationMessagesToLoad: [],
        currentConversationsBatchToLoad: [],
        currentConversationLoadingParams: null,
        conversationSubset: 'main',
        conversationState: 'main',
        selectedChannels: [],
        selectedTags: [],
        selectedAttribute: null,
        selectedAssignee: 'all',
        searchResultsMessages: [],
        searchResultMatches: [],
        needToCreateInitiaConversationAndContact: !!newRemoteUserId,

        fetchStatus: initialFetchStatus,
        connectedPage,
        activeChatbotId: botId,
        activeBotDefaultPageId,
        botLanguage,
      } as unknown as LcContext, // TODO: fix when time allows
      liveChatCRUDActions,
      contactsCRUDActions,
      sessionCRUDActions,
      history,
    );
  }, []);

  return (
    <LiveChatWithMachine lcMachine={lcMachine} key={lcMachine.id}>
      {children}
    </LiveChatWithMachine>
  );
};

type Props = {
  children: React.ReactNode;
  lcMachine: ReturnType<typeof createLivechatMachine>;
};

const LiveChatWithMachine = ({ lcMachine, children }: Props) => {
  const [lcMachineState, send, lcService] = useMachine<
    LcContext,
    LcEventSchema
  >(lcMachine);

  const { addLiveChatContext } = useLiveChatContext(lcMachine, lcService);

  useLiveChatServiceObserver(lcMachine, lcService);

  return <>{addLiveChatContext(children)}</>;
};
